import {
  ConnectorsDiff,
  CountryCodeMap,
  HashDiff,
  ObjectArrayDiff,
  SetDiff,
  StepsArrayDiff,
  ValueDiff,
  VerificationTemplateVersionArrayDiff,
  WorkflowVersionArrayDiff,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/types';
import { MatchRequirementProfile } from 'dashboard/models/match-requirement-profile';
import { ThemeAttributes } from 'dashboard/models/theme';
import { VerificationTemplateVersion } from 'dashboard/models/verification-template-version';
import { WorkflowVersion } from 'dashboard/models/workflow-version';
import { InquiryIso6391LanguageCode, InquiryLanguage } from 'lib/constants/locales';
import { FieldSchema } from 'lib/fields';
import { ApiModel, ApiRelationship, ApiResource } from 'lib/interfaces/api';
import DeviceHandoffOption from 'lib/interfaces/DeviceHandoffOption';
import { AutoClassificationConfig, StepPreview, UiStepType } from 'lib/interfaces/StepsConfig';
import { TransitionConfigAttributes, UiComponentConfig } from 'lib/interfaces/ui-components';
import {
  GovernmentIdEnabledIdClassByCountryEntry,
  SelfieMatch,
} from 'lib/interfaces/verificationConfig';
import {
  DocumentCaptureOptionDesktop,
  DocumentCaptureOptionMobile,
  GovernmentIdCaptureOptionDesktop,
  GovernmentIdCaptureOptionMobile,
  GovernmentIdCaptureOptionNativeMobile,
  SelfieCaptureOptionDesktop,
  SelfieCaptureOptionMobile,
} from 'verify/lib/interfaces/CaptureOption';
import { RedirectQueryStringConfig } from 'verify/models/inquiry';

export type InquiryTemplateVersionStepType =
  | UiStepType.Complete
  | UiStepType.Document
  | UiStepType.Integration
  | UiStepType.VerifyWithPersona
  | UiStepType.GovernmentId
  | UiStepType.Selfie
  | UiStepType.SelfieReflection
  | UiStepType.Ui
  | UiStepType.Loop;

type InquiryTemplateVersionStepConfig =
  | InquiryTemplateVersionStepCompleteConfig
  | InquiryTemplateVersionStepDocumentConfig
  | InquiryTemplateVersionStepIntegrationConfig
  | InquiryTemplateVersionStepVerifyWithPersonaConfig
  | InquiryTemplateVersionStepGovernmentIdConfig
  | InquiryTemplateVersionStepSelfieConfig
  | InquiryTemplateVersionStepUiConfig
  | InquiryTemplateVersionStepLoopConfig;

export type CombinedStepConfig =
  | InquiryTemplateVersionStepDocumentConfig
  | InquiryTemplateVersionStepGovernmentIdConfig
  | InquiryTemplateVersionStepSelfieConfig
  | InquiryTemplateVersionStepSelfieReflectionConfig;

// Keys for the different localizations in combined steps
export type LocalizationPageKey =
  | 'auto-classification-page'
  | 'capture-page'
  | 'check-page'
  | 'pending-page'
  | 'prompt-page'
  | 'select-page'
  | 'request-page'
  | 'review-page';

// Identifiers for the different pages in combined steps
export type CombinedStepPageIdentifier =
  | 'auto-classification-failure-page'
  | 'auto-classification-select-page'
  | 'capture-page'
  | 'check-page'
  | 'pending-page'
  | 'prompt-page'
  | 'select-page'
  | 'request-page'
  | 'review-page';

/**
 * Structure of customer supplied translation overrides
 * ex: { locale: 'en-US', value: 'My title in English.' }
 */
interface Translation {
  locale: InquiryLanguage;
  value: string | string[];
}

export type I18nDynamicVariables = null | Record<string, string | number | boolean | null>;

/**
 * Possible values for keys within a localization config section.
 *
 * See LocalizationWithI18nFallbackMap
 */
export type LocalizationValue =
  | null
  | undefined
  | Translation[]
  | string // 'inquiry.step.ui.government_id' -> '#{Hello you are %{age} old}'
  | I18nDynamicVariables; // i18n_fallback_translation_values

/**
 * Type covering possible config values. Ex select-page below:
 *
 *```
 * "localizations": {
 *   "select-page": {
 *      "title-translation": [ { "locale": "en-US", "value": "An english title"}],
 *      "title-default-value": "A default title",
 *      "title-fallback-i18n-key": "inquiry.step.government_id.select_page.title",
 *      "title-fallback-i18n-dynamic-variables": null,
 *   }
 * }
 * ```
 *
 * Note that previously, combined steps would only have the *_translation key, unlike UI steps
 * which would have all four. See translation_with_i18n_and_default_spec.rb
 */
export type LocalizationWithI18nFallbackMap = Record<string, LocalizationValue>;

/**
 * Structure of localization config sections in combined steps
 *
 * NOTE: This is being converted to LocalizationWithI18nFallbackMap
 */
export type LocalizationMap = Record<string, Translation[]>;

// StyleVariant is the style definitions for steps config
// Styles is the style defintion for steps preview
type StyleVariant = { [k: string]: any; namespace: string };
// FIXME: There style definitions for other step types missing
// type Styles = StepGovernmentIdStyles | StepSelfieStyles | StepUiStyles;

interface InquiryTemplateVersionStepBase {
  name: string;
  'name-display'?: string;
  description?: string;
  type: InquiryTemplateVersionStepType;
  config: InquiryTemplateVersionStepConfig | never;
  // TODO: generate pseudo random token on server? makes typing way easier.
  // Should also probably be optional.
  token: string;
  'style-variants'?: StyleVariant[];
}

export interface InquiryTemplateVersionStepStatus {
  name: string;
  initial: boolean;
  terminal: boolean;
}

type DeviceHandoffPromptPageConfigLocalizations = {
  'title-default-value': string | null;
  'title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
  'title-fallback-i18n-key': string | null;
  'title-translations'?: Translation[];
  'description-default-value': string | null;
  'description-fallback-i18n-dynamic-variables': I18nDynamicVariables;
  'description-fallback-i18n-key': string | null;
  'description-translations'?: Translation[];
  'qr-code-default-value': string | null;
  'qr-code-fallback-i18n-dynamic-variables': I18nDynamicVariables;
  'qr-code-fallback-i18n-key': string | null;
  'qr-code-translations'?: Translation[];
  'via-email-default-value': string | null;
  'via-email-fallback-i18n-dynamic-variables': I18nDynamicVariables;
  'via-email-fallback-i18n-key': string | null;
  'via-email-translations'?: Translation[];
  'via-sms-default-value': string | null;
  'via-sms-fallback-i18n-dynamic-variables': I18nDynamicVariables;
  'via-sms-fallback-i18n-key': string | null;
  'via-sms-translations'?: Translation[];
};

type DeviceHandoffStartedPageConfigLocalizations = {
  'title-default-value': string | null;
  'title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
  'title-fallback-i18n-key': string | null;
  'title-translations'?: Translation[];
  'prompt-default-value': string | null;
  'prompt-fallback-i18n-dynamic-variables': I18nDynamicVariables;
  'prompt-fallback-i18n-key': string | null;
  'prompt-translations'?: Translation[];
  'btn-resume-default-value': string | null;
  'btn-resume-fallback-i18n-dynamic-variables': I18nDynamicVariables;
  'btn-resume-fallback-i18n-key': string | null;
  'btn-resume-translations'?: Translation[];
};

type DeviceHandoffEmailPromptPageConfigLocalizations = {
  'title-default-value': string | null;
  'title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
  'title-fallback-i18n-key': string | null;
  'title-translations'?: Translation[];
  'prompt-default-value': string | null;
  'prompt-fallback-i18n-dynamic-variables': I18nDynamicVariables;
  'prompt-fallback-i18n-key': string | null;
  'prompt-translations'?: Translation[];
  'btn-submit-default-value': string | null;
  'btn-submit-fallback-i18n-dynamic-variables': I18nDynamicVariables;
  'btn-submit-fallback-i18n-key': string | null;
  'btn-submit-translations'?: Translation[];
  'disclaimer-default-value': string | null;
  'disclaimer-fallback-i18n-dynamic-variables': I18nDynamicVariables;
  'disclaimer-fallback-i18n-key': string | null;
  'disclaimer-translations'?: Translation[];
};

type DeviceHandoffSmsPromptPageConfigLocalizations = {
  'title-default-value': string | null;
  'title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
  'title-fallback-i18n-key': string | null;
  'title-translations'?: Translation[];
  'prompt-default-value': string | null;
  'prompt-fallback-i18n-dynamic-variables': I18nDynamicVariables;
  'prompt-fallback-i18n-key': string | null;
  'prompt-translations'?: Translation[];
  'btn-submit-default-value': string | null;
  'btn-submit-fallback-i18n-dynamic-variables': I18nDynamicVariables;
  'btn-submit-fallback-i18n-key': string | null;
  'btn-submit-translations'?: Translation[];
  'disclaimer-default-value': string | null;
  'disclaimer-fallback-i18n-dynamic-variables': I18nDynamicVariables;
  'disclaimer-fallback-i18n-key': string | null;
  'disclaimer-translations'?: Translation[];
};

type DeviceHandoffSmsSentPageConfigLocalizations = {
  'title-default-value': string | null;
  'title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
  'title-fallback-i18n-key': string | null;
  'title-translations'?: Translation[];
  'prompt-default-value': string | null;
  'prompt-fallback-i18n-dynamic-variables': I18nDynamicVariables;
  'prompt-fallback-i18n-key': string | null;
  'prompt-translations'?: Translation[];
  'btn-resend-default-value': string | null;
  'btn-resend-fallback-i18n-dynamic-variables': I18nDynamicVariables;
  'btn-resend-fallback-i18n-key': string | null;
  'btn-resend-translations'?: Translation[];
};

type DeviceHandoffEmailSentPageConfigLocalizations = {
  'title-default-value': string | null;
  'title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
  'title-fallback-i18n-key': string | null;
  'title-translations'?: Translation[];
  'prompt-default-value': string | null;
  'prompt-fallback-i18n-dynamic-variables': I18nDynamicVariables;
  'prompt-fallback-i18n-key': string | null;
  'prompt-translations'?: Translation[];
  'btn-resend-default-value': string | null;
  'btn-resend-fallback-i18n-dynamic-variables': I18nDynamicVariables;
  'btn-resend-fallback-i18n-key': string | null;
  'btn-resend-translations'?: Translation[];
};

export type DeviceHandoffConfigLocalizations = {
  'device-handoff-prompt-page': DeviceHandoffPromptPageConfigLocalizations;
  'device-handoff-started-page': DeviceHandoffStartedPageConfigLocalizations;
  'device-handoff-email-prompt-page': DeviceHandoffEmailPromptPageConfigLocalizations;
  'device-handoff-sms-prompt-page': DeviceHandoffSmsPromptPageConfigLocalizations;
  'device-handoff-sms-sent-page': DeviceHandoffSmsSentPageConfigLocalizations;
  'device-handoff-email-sent-page': DeviceHandoffEmailSentPageConfigLocalizations;
};

export type CombinedStepBaseConfig = TransitionConfigAttributes & {
  'back-step'?: string;
  'verification-template-version-id': string;
  'device-handoff-options': DeviceHandoffOption[];
  'device-handoff-list-style'?: 'outline' | 'outline_light' | 'button';
  'field-key-device-handoff-email': string | null;
  'field-key-device-handoff-phone-number': string | null;
  localizations: Record<string, LocalizationWithI18nFallbackMap>;
  statuses: InquiryTemplateVersionStepStatus[];
};

export interface InquiryTemplateVersionStepCompleteConfig {
  statuses: InquiryTemplateVersionStepStatus[];
  localizations: {
    'child-flow-page': {
      'prompt-default-value': string | null;
      'prompt-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'prompt-fallback-i18n-key': string | null;
      'prompt-translations'?: Translation[];
      'title-default-value': string | null;
      'title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'title-fallback-i18n-key': string | null;
      'title-translations'?: Translation[];
    };
  };
}

export interface InquiryTemplateVersionStepDocumentConfig extends CombinedStepBaseConfig {
  kind?: string;
  'enabled-capture-options-desktop': DocumentCaptureOptionDesktop[];
  'enabled-capture-options-mobile': DocumentCaptureOptionMobile[];
  localizations: DeviceHandoffConfigLocalizations & {
    'capture-page': {
      'btn-additional-photo-default-value': string | null;
      'btn-additional-photo-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'btn-additional-photo-fallback-i18n-key': string | null;
      'btn-additional-photo-translations'?: Translation[];
      'btn-finish-default-value': string | null;
      'btn-finish-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'btn-finish-fallback-i18n-key': string | null;
      'btn-finish-translations'?: Translation[];
      'btn-take-photo-default-value': string | null;
      'btn-take-photo-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'btn-take-photo-fallback-i18n-key': string | null;
      'btn-take-photo-translations'?: Translation[];
      'prompt-default-value': string | null;
      'prompt-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'prompt-fallback-i18n-key': string | null;
      'prompt-translations'?: Translation[];
    };
    'check-page': {
      'btn-retake-default-value': string | null;
      'btn-retake-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'btn-retake-fallback-i18n-key': string | null;
      'btn-retake-translations'?: Translation[];
      'btn-submit-default-value': string | null;
      'btn-submit-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'btn-submit-fallback-i18n-key': string | null;
      'btn-submit-translations'?: Translation[];
      'confirm-upload-btn-retake-default-value': string | null;
      'confirm-upload-btn-retake-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'confirm-upload-btn-retake-fallback-i18n-key': string | null;
      'confirm-upload-btn-retake-translations'?: Translation[];
      'confirm-upload-btn-submit-default-value': string | null;
      'confirm-upload-btn-submit-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'confirm-upload-btn-submit-fallback-i18n-key': string | null;
      'confirm-upload-btn-submit-translations'?: Translation[];
      'confirm-upload-prompt-default-value': string | null;
      'confirm-upload-prompt-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'confirm-upload-prompt-fallback-i18n-key': string | null;
      'confirm-upload-prompt-translations'?: Translation[];
      'confirm-upload-title-default-value': string | null;
      'confirm-upload-title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'confirm-upload-title-fallback-i18n-key': string | null;
      'confirm-upload-title-translations'?: Translation[];
      'confirm-upload-uploading-default-value': string | null;
      'confirm-upload-uploading-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'confirm-upload-uploading-fallback-i18n-key': string | null;
      'confirm-upload-uploading-translations'?: Translation[];
      'large-file-upload-default-value': string | null;
      'large-file-upload-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'large-file-upload-fallback-i18n-key': string | null;
      'large-file-upload-translations'?: Translation[];
      'large-image-upload-default-value': string | null;
      'large-image-upload-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'large-image-upload-fallback-i18n-key': string | null;
      'large-image-upload-translations'?: Translation[];
      'prompt-default-value': string | null;
      'prompt-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'prompt-fallback-i18n-key': string | null;
      'prompt-translations'?: Translation[];
      'title-default-value': string | null;
      'title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'title-fallback-i18n-key': string | null;
      'title-translations'?: Translation[];
      'uploading-default-value': string | null;
      'uploading-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'uploading-fallback-i18n-key': string | null;
      'uploading-translations'?: Translation[];
    };
    'pending-page': {
      'description-default-value': string | null;
      'description-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'description-fallback-i18n-key': string | null;
      'description-translations'?: Translation[];
      'title-default-value': string | null;
      'title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'title-fallback-i18n-key': string | null;
      'title-translations'?: Translation[];
    };
    'prompt-page': {
      'btn-cancel-default-value': string | null;
      'btn-cancel-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'btn-cancel-fallback-i18n-key': string | null;
      'btn-cancel-translations'?: Translation[];
      'btn-capture-default-value': string | null;
      'btn-capture-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'btn-capture-fallback-i18n-key': string | null;
      'btn-capture-translations'?: Translation[];
      'btn-delete-default-value': string | null;
      'btn-delete-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'btn-delete-fallback-i18n-key': string | null;
      'btn-delete-translations'?: Translation[];
      'btn-device-handoff-default-value': string | null;
      'btn-device-handoff-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'btn-device-handoff-fallback-i18n-key': string | null;
      'btn-device-handoff-translations'?: Translation[];
      'btn-submit-default-value': string | null;
      'btn-submit-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'btn-submit-fallback-i18n-key': string | null;
      'btn-submit-translations'?: Translation[];
      'btn-upload-default-value': string | null;
      'btn-upload-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'btn-upload-fallback-i18n-key': string | null;
      'btn-upload-translations'?: Translation[];
      'camera-permissions-btn-cancel-translations'?: Translation[];
      'camera-permissions-btn-cancel-default-value': string | null;
      'camera-permissions-btn-cancel-fallback-i18n-key': string | null;
      'camera-permissions-btn-cancel-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'camera-permissions-btn-continue-mobile-default-value': string | null;
      'camera-permissions-btn-continue-mobile-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'camera-permissions-btn-continue-mobile-fallback-i18n-key': string | null;
      'camera-permissions-btn-continue-mobile-translations'?: Translation[];
      'camera-permissions-btn-continue-web-default-value': string | null;
      'camera-permissions-btn-continue-web-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'camera-permissions-btn-continue-web-fallback-i18n-key': string | null;
      'camera-permissions-btn-continue-web-translations'?: Translation[];
      'camera-permissions-prompt-default-value': string | null;
      'camera-permissions-prompt-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'camera-permissions-prompt-fallback-i18n-key': string | null;
      'camera-permissions-prompt-translations'?: Translation[];
      'camera-permissions-title-default-value': string | null;
      'camera-permissions-title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'camera-permissions-title-fallback-i18n-key': string | null;
      'camera-permissions-title-translations'?: Translation[];
      'capture-options-dialog-title-default-value': string | null;
      'capture-options-dialog-title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'capture-options-dialog-title-fallback-i18n-key': string | null;
      'capture-options-dialog-title-translations'?: Translation[];
      'photos-permissions-body-default-value': string | null;
      'photos-permissions-body-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'photos-permissions-body-fallback-i18n-key': string | null;
      'photos-permissions-body-translations'?: Translation[];
      'photos-permissions-dismiss-button-text-default-value': string | null;
      'photos-permissions-dismiss-button-text-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'photos-permissions-dismiss-button-text-fallback-i18n-key': string | null;
      'photos-permissions-dismiss-button-text-translations'?: Translation[];
      'photos-permissions-enable-button-text-default-value': string | null;
      'photos-permissions-enable-button-text-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'photos-permissions-enable-button-text-fallback-i18n-key': string | null;
      'photos-permissions-enable-button-text-translations'?: Translation[];
      'photos-permissions-settings-button-text-default-value': string | null;
      'photos-permissions-settings-button-text-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'photos-permissions-settings-button-text-fallback-i18n-key': string | null;
      'photos-permissions-settings-button-text-translations'?: Translation[];
      'photos-permissions-settings-text-default-value': string | null;
      'photos-permissions-settings-text-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'photos-permissions-settings-text-fallback-i18n-key': string | null;
      'photos-permissions-settings-text-translations'?: Translation[];
      'photos-permissions-title-default-value': string | null;
      'photos-permissions-title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'photos-permissions-title-fallback-i18n-key': string | null;
      'photos-permissions-title-translations'?: Translation[];
      'delete-dialog-title-default-value': string | null;
      'delete-dialog-title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'delete-dialog-title-fallback-i18n-key': string | null;
      'delete-dialog-title-translations'?: Translation[];
      'no-capture-options-prompt-default-value': string | null;
      'no-capture-options-prompt-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'no-capture-options-prompt-fallback-i18n-key': string | null;
      'no-capture-options-prompt-translations'?: Translation[];
      'prompt-default-value': string | null;
      'prompt-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'prompt-fallback-i18n-key': string | null;
      'prompt-translations'?: Translation[];
      'title-default-value': string | null;
      'title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'title-fallback-i18n-key': string | null;
      'title-translations'?: Translation[];
    };
  };
}

export interface InquiryTemplateVersionStepGovernmentIdConfig extends CombinedStepBaseConfig {
  disclaimer: 'default' | 'none';
  'enabled-skip-unknown': boolean;
  'field-key-country': string;
  'field-key-document': string;
  'field-key-idclass': string;
  'govid-design-version': number;
  'enabled-capture-options-desktop': GovernmentIdCaptureOptionDesktop[];
  'enabled-capture-options-mobile': GovernmentIdCaptureOptionMobile[];
  'enabled-capture-options-native-mobile': GovernmentIdCaptureOptionNativeMobile[];
  'always-show-mobile-camera-capture-option': boolean;
  'auto-classification-config': AutoClassificationConfig | null;
  idclasses: GovernmentIdEnabledIdClassByCountryEntry[];
  localizations: DeviceHandoffConfigLocalizations & {
    'capture-page': {
      'disclaimer-translations'?: Translation[];
      'disclaimer-fallback-i18n-key': 'inquiry.step.government_id.capture_page.disclaimer';
      'disclaimer-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'disclaimer-default-value': null;
      'btn-take-photo-default-value': string | null;
      'btn-take-photo-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'btn-take-photo-fallback-i18n-key': string | null;
      'btn-take-photo-translations'?: Translation[];
      'capturing-translations'?: Translation[];
      'capturing-default-value': string | null;
      'capturing-fallback-i18n-key': string | null;
      'capturing-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'confirm-capture-translations'?: Translation[];
      'confirm-capture-default-value': string | null;
      'confirm-capture-fallback-i18n-key': string | null;
      'confirm-capture-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'scan-back-translations'?: Translation[];
      'scan-back-default-value': string | null;
      'scan-back-fallback-i18n-key': string | null;
      'scan-back-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'scan-front-or-back-translations'?: Translation[];
      'scan-front-or-back-default-value': string | null;
      'scan-front-or-back-fallback-i18n-key': string | null;
      'scan-front-or-back-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'scan-front-translations'?: Translation[];
      'scan-front-default-value': string | null;
      'scan-front-fallback-i18n-key': string | null;
      'scan-front-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'scan-mrz-translations'?: Translation[];
      'scan-mrz-default-value': string | null;
      'scan-mrz-fallback-i18n-key': string | null;
      'scan-mrz-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'scan-pdf417-translations'?: Translation[];
      'scan-pdf417-default-value': string | null;
      'scan-pdf417-fallback-i18n-key': string | null;
      'scan-pdf417-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'scan-signature-translations'?: Translation[];
      'scan-signature-default-value': string | null;
      'scan-signature-fallback-i18n-key': string | null;
      'scan-signature-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'prompt-override-translations'?: Translation[];
      'prompt-override-default-value': string | null;
      'prompt-override-fallback-i18n-key': string | null;
      'prompt-override-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'title-translations'?: Translation[];
      'title-default-value': string | null;
      'title-fallback-i18n-key': string | null;
      'title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
    };
    'check-page': {
      'title-translations'?: Translation[];
      'title-default-value': string | null;
      'title-fallback-i18n-key': string | null;
      'title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'title-confirm-capture-translations'?: Translation[];
      'title-confirm-capture-default-value': string | null;
      'title-confirm-capture-fallback-i18n-key': string | null;
      'title-confirm-capture-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'prompt-translations'?: Translation[];
      'prompt-default-value': string | null;
      'prompt-fallback-i18n-key': string | null;
      'prompt-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'uploading-translations'?: Translation[];
      'uploading-default-value': string | null;
      'uploading-fallback-i18n-key': string | null;
      'uploading-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'large-upload-translations'?: Translation[];
      'large-upload-default-value': string | null;
      'large-upload-fallback-i18n-key': string | null;
      'large-upload-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'btn-submit-translations'?: Translation[];
      'btn-submit-default-value': string | null;
      'btn-submit-fallback-i18n-key': string | null;
      'btn-submit-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'btn-retake-translations'?: Translation[];
      'btn-retake-default-value': string | null;
      'btn-retake-fallback-i18n-key': string | null;
      'btn-retake-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'confirm-title-translations'?: Translation[];
      'confirm-title-default-value': string | null;
      'confirm-title-fallback-i18n-key': string | null;
      'confirm-title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'confirm-prompt-translations'?: Translation[];
      'confirm-prompt-default-value': string | null;
      'confirm-prompt-fallback-i18n-key': string | null;
      'confirm-prompt-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'confirm-uploading-translations'?: Translation[];
      'confirm-uploading-default-value': string | null;
      'confirm-uploading-fallback-i18n-key': string | null;
      'confirm-uploading-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'confirm-btn-submit-translations'?: Translation[];
      'confirm-btn-submit-default-value': string | null;
      'confirm-btn-submit-fallback-i18n-key': string | null;
      'confirm-btn-submit-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'confirm-btn-retake-translations'?: Translation[];
      'confirm-btn-retake-default-value': string | null;
      'confirm-btn-retake-fallback-i18n-key': string | null;
      'confirm-btn-retake-fallback-i18n-dynamic-variables': I18nDynamicVariables;
    };
    'pending-page': {
      'title-translations'?: Translation[];
      'title-default-value': string | null;
      'title-fallback-i18n-key': string | null;
      'title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'description-translations'?: Translation[];
      'description-default-value': string | null;
      'description-fallback-i18n-key': string | null;
      'description-fallback-i18n-dynamic-variables': I18nDynamicVariables;
    };
    'prompt-page': {
      'btn-capture-translations'?: Translation[];
      'btn-capture-default-value': string | null;
      'btn-capture-fallback-i18n-key': string | null;
      'btn-capture-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'btn-capture-mobile-camera-translations'?: Translation[];
      'btn-capture-mobile-camera-default-value': string | null;
      'btn-capture-mobile-camera-fallback-i18n-key': string | null;
      'btn-capture-mobile-camera-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'btn-device-handoff-translations'?: Translation[];
      'btn-device-handoff-default-value': string | null;
      'btn-device-handoff-fallback-i18n-key': string | null;
      'btn-device-handoff-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'btn-upload-translations'?: Translation[];
      'btn-upload-default-value': string | null;
      'btn-upload-fallback-i18n-key': string | null;
      'btn-upload-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'camera-permissions-btn-cancel-translations'?: Translation[];
      'camera-permissions-btn-cancel-default-value': string | null;
      'camera-permissions-btn-cancel-fallback-i18n-key': string | null;
      'camera-permissions-btn-cancel-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'camera-permissions-btn-continue-mobile-translations'?: Translation[];
      'camera-permissions-btn-continue-mobile-default-value': string | null;
      'camera-permissions-btn-continue-mobile-fallback-i18n-key': string | null;
      'camera-permissions-btn-continue-mobile-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'camera-permissions-btn-continue-web-translations'?: Translation[];
      'camera-permissions-btn-continue-web-default-value': string | null;
      'camera-permissions-btn-continue-web-fallback-i18n-key': string | null;
      'camera-permissions-btn-continue-web-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'camera-permissions-prompt-translations'?: Translation[];
      'camera-permissions-prompt-default-value': string | null;
      'camera-permissions-prompt-fallback-i18n-key': string | null;
      'camera-permissions-prompt-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'camera-permissions-title-translations'?: Translation[];
      'camera-permissions-title-default-value': string | null;
      'camera-permissions-title-fallback-i18n-key': string | null;
      'camera-permissions-title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'photos-permissions-body-default-value': string | null;
      'photos-permissions-body-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'photos-permissions-body-fallback-i18n-key': string | null;
      'photos-permissions-body-translations'?: Translation[];
      'photos-permissions-dismiss-button-text-default-value': string | null;
      'photos-permissions-dismiss-button-text-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'photos-permissions-dismiss-button-text-fallback-i18n-key': string | null;
      'photos-permissions-dismiss-button-text-translations'?: Translation[];
      'photos-permissions-enable-button-text-default-value': string | null;
      'photos-permissions-enable-button-text-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'photos-permissions-enable-button-text-fallback-i18n-key': string | null;
      'photos-permissions-enable-button-text-translations'?: Translation[];
      'photos-permissions-settings-button-text-default-value': string | null;
      'photos-permissions-settings-button-text-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'photos-permissions-settings-button-text-fallback-i18n-key': string | null;
      'photos-permissions-settings-button-text-translations'?: Translation[];
      'photos-permissions-settings-text-default-value': string | null;
      'photos-permissions-settings-text-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'photos-permissions-settings-text-fallback-i18n-key': string | null;
      'photos-permissions-settings-text-translations'?: Translation[];
      'photos-permissions-title-default-value': string | null;
      'photos-permissions-title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'photos-permissions-title-fallback-i18n-key': string | null;
      'photos-permissions-title-translations'?: Translation[];
      'no-capture-options-prompt-translations'?: Translation[];
      'no-capture-options-prompt-fallback-i18n-key': string | null;
      'no-capture-options-prompt-fallback-i18n-dynamic-variables': null | string | null;
      'no-capture-options-prompt-default-value': Record<string, string | number | boolean | null>;
    };
    'select-page': {
      'disclaimer-translations'?: Translation[];
      'disclaimer-default-value': string | null;
      'disclaimer-fallback-i18n-key': string | null;
      'disclaimer-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'option-notice-translations'?: Translation[];
      'option-notice-default-value': string | null;
      'option-notice-fallback-i18n-key': string | null;
      'option-notice-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'prompt-translations'?: Translation[];
      'prompt-default-value': string | null;
      'prompt-fallback-i18n-key': string | null;
      'prompt-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'title-translations'?: Translation[];
      'title-default-value': string | null;
      'title-fallback-i18n-key': string | null;
      'title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'unsupported-title-translations'?: Translation[];
      'unsupported-title-default-value': string | null;
      'unsupported-title-fallback-i18n-key': string | null;
      'unsupported-title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'unsupported-prompt-translations'?: Translation[];
      'unsupported-prompt-default-value': string | null;
      'unsupported-prompt-fallback-i18n-key': string | null;
      'unsupported-prompt-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      // ID types
      'cct-translations'?: Translation[];
      'cct-default-value': string | null;
      'cct-fallback-i18n-key': string | null;
      'cct-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'cid-translations'?: Translation[];
      'cid-default-value': string | null;
      'cid-fallback-i18n-key': string | null;
      'cid-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'dl-translations'?: Translation[];
      'dl-default-value': string | null;
      'dl-fallback-i18n-key': string | null;
      'dl-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'foid-translations'?: Translation[];
      'foid-default-value': string | null;
      'foid-fallback-i18n-key': string | null;
      'foid-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'hic-translations'?: Translation[];
      'hic-default-value': string | null;
      'hic-fallback-i18n-key': string | null;
      'hic-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'id-translations'?: Translation[];
      'id-default-value': string | null;
      'id-fallback-i18n-key': string | null;
      'id-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'ipp-translations'?: Translation[];
      'ipp-default-value': string | null;
      'ipp-fallback-i18n-key': string | null;
      'ipp-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'keyp-translations'?: Translation[];
      'keyp-default-value': string | null;
      'keyp-fallback-i18n-key': string | null;
      'keyp-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'ltpass-translations'?: Translation[];
      'ltpass-default-value': string | null;
      'ltpass-fallback-i18n-key': string | null;
      'ltpass-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'mid-translations'?: Translation[];
      'mid-default-value': string | null;
      'mid-fallback-i18n-key': string | null;
      'mid-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'munid-translations'?: Translation[];
      'munid-default-value': string | null;
      'munid-fallback-i18n-key': string | null;
      'munid-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'myn-translations'?: Translation[];
      'myn-default-value': string | null;
      'myn-fallback-i18n-key': string | null;
      'myn-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'nbi-translations'?: Translation[];
      'nbi-default-value': string | null;
      'nbi-fallback-i18n-key': string | null;
      'nbi-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'nric-translations'?: Translation[];
      'nric-default-value': string | null;
      'nric-fallback-i18n-key': string | null;
      'nric-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'ofw-translations'?: Translation[];
      'ofw-default-value': string | null;
      'ofw-fallback-i18n-key': string | null;
      'ofw-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'pan-translations'?: Translation[];
      'pan-default-value': string | null;
      'pan-fallback-i18n-key': string | null;
      'pan-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'pid-translations'?: Translation[];
      'pid-default-value': string | null;
      'pid-fallback-i18n-key': string | null;
      'pid-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'pp-translations'?: Translation[];
      'pp-default-value': string | null;
      'pp-fallback-i18n-key': string | null;
      'pp-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'ppc-translations'?: Translation[];
      'ppc-default-value': string | null;
      'ppc-fallback-i18n-key': string | null;
      'ppc-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'pr-translations'?: Translation[];
      'pr-default-value': string | null;
      'pr-fallback-i18n-key': string | null;
      'pr-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'rp-translations'?: Translation[];
      'rp-default-value': string | null;
      'rp-fallback-i18n-key': string | null;
      'rp-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'sss-translations'?: Translation[];
      'sss-default-value': string | null;
      'sss-fallback-i18n-key': string | null;
      'sss-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'td-translations'?: Translation[];
      'td-default-value': string | null;
      'td-fallback-i18n-key': string | null;
      'td-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'tribalid-translations'?: Translation[];
      'tribalid-default-value': string | null;
      'tribalid-fallback-i18n-key': string | null;
      'tribalid-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'umid-translations'?: Translation[];
      'umid-default-value': string | null;
      'umid-fallback-i18n-key': string | null;
      'umid-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'unknown-translations'?: Translation[];
      'unknown-default-value': string | null;
      'unknown-fallback-i18n-key': string | null;
      'unknown-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'us-id-translations'?: Translation[];
      'us-id-default-value': string | null;
      'us-id-fallback-i18n-key': string | null;
      'us-id-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'vid-translations'?: Translation[];
      'vid-default-value': string | null;
      'vid-fallback-i18n-key': string | null;
      'vid-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'visa-translations'?: Translation[];
      'visa-default-value': string | null;
      'visa-fallback-i18n-key': string | null;
      'visa-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'wp-translations'?: Translation[];
      'wp-default-value': string | null;
      'wp-fallback-i18n-key': string | null;
      'wp-fallback-i18n-dynamic-variables': I18nDynamicVariables;
    };
    'request-page': {
      'choose-photo-button-text-translations'?: Translation[];
      'choose-photo-button-text-fallback-i18n-key': string | null;
      'choose-photo-button-text-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'choose-photo-button-text-default-value': string | null;
      'description-back-translations'?: Translation[];
      'description-back-fallback-i18n-key': string | null;
      'description-back-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'description-back-default-value': string | null;
      'description-front-translations'?: Translation[];
      'description-front-fallback-i18n-key': string | null;
      'description-front-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'description-front-default-value': string | null;
      'description-pdf417-translations'?: Translation[];
      'description-pdf417-fallback-i18n-key': string | null;
      'description-pdf417-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'description-pdf417-default-value': string | null;
      'description-passport-signature-translations'?: Translation[];
      'description-passport-signature-fallback-i18n-key': string | null;
      'description-passport-signature-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'description-passport-signature-default-value': string | null;
      'live-upload-button-text-translations'?: Translation[];
      'live-upload-button-text-fallback-i18n-key': string | null;
      'live-upload-button-text-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'live-upload-button-text-default-value': string | null;
      'title-back-translations'?: Translation[];
      'title-back-fallback-i18n-key': string | null;
      'title-back-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'title-back-default-value': string | null;
      'title-front-translations'?: Translation[];
      'title-front-fallback-i18n-key': string | null;
      'title-front-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'title-front-default-value': string | null;
      'title-pdf417-translations'?: Translation[];
      'title-pdf417-fallback-i18n-key': string | null;
      'title-pdf417-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'title-pdf417-default-value': string | null;
      'title-passport-signature-translations'?: Translation[];
      'title-passport-signature-fallback-i18n-key': string | null;
      'title-passport-signature-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'title-passport-signature-default-value': string | null;
    };
    'review-upload-page': {
      'choose-another-button-text-translations'?: Translation[];
      'choose-another-button-text-fallback-i18n-key': string | null;
      'choose-another-button-text-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'choose-another-button-text-default-value': string | null;
      'confirm-button-text-translations'?: Translation[];
      'confirm-button-text-fallback-i18n-key': string | null;
      'confirm-button-text-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'confirm-button-text-default-value': string | null;
      'description-back-translations'?: Translation[];
      'description-back-fallback-i18n-key': string | null;
      'description-back-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'description-back-default-value': string | null;
      'description-front-translations'?: Translation[];
      'description-front-fallback-i18n-key': string | null;
      'description-front-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'description-front-default-value': string | null;
      'description-pdf417-translations'?: Translation[];
      'description-pdf417-fallback-i18n-key': string | null;
      'description-pdf417-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'description-pdf417-default-value': string | null;
      'description-passport-signature-translations'?: Translation[];
      'description-passport-signature-fallback-i18n-key': string | null;
      'description-passport-signature-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'description-passport-signature-default-value': string | null;
      'title-back-translations'?: Translation[];
      'title-back-fallback-i18n-key': string | null;
      'title-back-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'title-back-default-value': string | null;
      'title-front-translations'?: Translation[];
      'title-front-fallback-i18n-key': string | null;
      'title-front-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'title-front-default-value': string | null;
      'title-pdf417-translations'?: Translation[];
      'title-pdf417-fallback-i18n-key': string | null;
      'title-pdf417-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'title-pdf417-default-value': string | null;
      'title-passport-signature-translations'?: Translation[];
      'title-passport-signature-fallback-i18n-key': string | null;
      'title-passport-signature-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'title-passport-signature-default-value': string | null;
    };
    'auto-classification-page': {
      'unable-to-classify-document-title-translations'?: Translation[];
      'unable-to-classify-document-title-fallback-i18n-key': string | null;
      'unable-to-classify-document-title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'unable-to-classify-document-title-default-value': string | null;
      'unable-to-classify-document-continue-button-text-translations'?: Translation[];
      'unable-to-classify-document-continue-button-text-fallback-i18n-key': string | null;
      'unable-to-classify-document-continue-button-text-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'unable-to-classify-document-continue-button-text-default-value': string | null;
      'id-class-rejected-title-translations'?: Translation[];
      'id-class-rejected-title-fallback-i18n-key': string | null;
      'id-class-rejected-title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'id-class-rejected-title-default-value': string | null;
      'id-class-rejected-continue-button-text-translations'?: Translation[];
      'id-class-rejected-continue-button-text-fallback-i18n-key': string | null;
      'id-class-rejected-continue-button-text-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'id-class-rejected-continue-button-text-default-value': string | null;
      'country-input-title-translations'?: Translation[];
      'country-input-title-fallback-i18n-key': string | null;
      'country-input-title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'country-input-title-default-value': string | null;
      'id-class-input-title-translations'?: Translation[];
      'id-class-input-title-fallback-i18n-key': string | null;
      'id-class-input-title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'id-class-input-title-default-value': string | null;
      'manual-classification-title-translations'?: Translation[];
      'manual-classification-title-fallback-i18n-key': string | null;
      'manual-classification-title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'manual-classification-title-default-value': string | null;
      'manual-classification-continue-button-text-translations'?: Translation[];
      'manual-classification-continue-button-text-fallback-i18n-key': string | null;
      'manual-classification-continue-button-text-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'manual-classification-continue-button-text-default-value': string | null;
      'auto-classification-capture-tip-text-translations'?: Translation[];
      'auto-classification-capture-tip-text-fallback-i18n-key': string | null;
      'auto-classification-capture-tip-text-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'auto-classification-capture-tip-text-default-value': string | null;
    };
  };
}

export interface InquiryTemplateVersionStepSelfieConfig extends CombinedStepBaseConfig {
  'selfie-type': SelfieMatch;
  'field-key-selfie': string;
  'web-camera-allow-stillfie-fallback': boolean;
  'enabled-capture-options-desktop': SelfieCaptureOptionDesktop[];
  'enabled-capture-options-mobile': SelfieCaptureOptionMobile[];
  localizations: DeviceHandoffConfigLocalizations & {
    'capture-page': {
      'title-translations'?: Translation[];
      'title-default-value': string | null;
      'title-fallback-i18n-key': string | null;
      'title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'selfie-hint-center-delayed-default-value': null;
      'selfie-hint-center-delayed-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'selfie-hint-center-delayed-fallback-i18n-key': string | null;
      'selfie-hint-center-delayed-translations'?: Translation[];
      'selfie-hint-center-face-default-value': null;
      'selfie-hint-center-face-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'selfie-hint-center-face-fallback-i18n-key': string | null;
      'selfie-hint-center-face-translations'?: Translation[];
      'selfie-hint-hold-still-default-value': null;
      'selfie-hint-hold-still-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'selfie-hint-hold-still-fallback-i18n-key': string | null;
      'selfie-hint-hold-still-translations'?: Translation[];
      'selfie-hint-look-left-default-value': null;
      'selfie-hint-look-left-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'selfie-hint-look-left-fallback-i18n-key': string | null;
      'selfie-hint-look-left-translations'?: Translation[];
      'selfie-hint-look-right-default-value': null;
      'selfie-hint-look-right-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'selfie-hint-look-right-fallback-i18n-key': string | null;
      'selfie-hint-look-right-translations'?: Translation[];
      'selfie-hint-face-too-close-default-value': null;
      'selfie-hint-face-too-close-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'selfie-hint-face-too-close-fallback-i18n-key': string | null;
      'selfie-hint-face-too-close-translations'?: Translation[];
      'selfie-hint-face-too-far-default-value': null;
      'selfie-hint-face-too-far-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'selfie-hint-face-too-far-fallback-i18n-key': string | null;
      'selfie-hint-face-too-far-translations'?: Translation[];
      'selfie-hint-multiple-faces-default-value': null;
      'selfie-hint-multiple-faces-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'selfie-hint-multiple-faces-fallback-i18n-key': string | null;
      'selfie-hint-multiple-faces-translations'?: Translation[];
      'selfie-hint-pose-not-center-default-value': null;
      'selfie-hint-pose-not-center-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'selfie-hint-pose-not-center-fallback-i18n-key': string | null;
      'selfie-hint-pose-not-center-translations'?: Translation[];
      'selfie-hint-take-photo-default-value': null;
      'selfie-hint-take-photo-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'selfie-hint-take-photo-fallback-i18n-key': string | null;
      'selfie-hint-take-photo-translations'?: Translation[];
      'selfie-hint-verifying-default-value': null;
      'selfie-hint-verifying-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'selfie-hint-verifying-fallback-i18n-key': string | null;
      'selfie-hint-verifying-translations'?: Translation[];
    };
    'pending-page': {
      'title-translations'?: Translation[];
      'title-default-value': string | null;
      'title-fallback-i18n-key': string | null;
      'title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'description-translations'?: Translation[];
      'description-default-value': string | null;
      'description-fallback-i18n-key': string | null;
      'description-fallback-i18n-dynamic-variables': I18nDynamicVariables;
    };
    'prompt-page': {
      // omit selfie biometrics from editable translations
      'agree-to-policy-default-value': string | null;
      'agree-to-policy-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'agree-to-policy-fallback-i18n-key': string | null;
      'agree-to-policy-translations'?: Translation[];
      'btn-device-handoff-default-value': string | null;
      'btn-device-handoff-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'btn-device-handoff-fallback-i18n-key': string | null;
      'btn-device-handoff-translations'?: Translation[];
      'btn-submit-default-value': string | null;
      'btn-submit-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'btn-submit-fallback-i18n-key': string | null;
      'btn-submit-translations'?: Translation[];
      'no-capture-options-prompt-default-value': string | null;
      'no-capture-options-prompt-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'no-capture-options-prompt-fallback-i18n-key': string | null;
      'no-capture-options-prompt-translations'?: Translation[];
      'selfie-prompt-center-default-value': string | null;
      'selfie-prompt-center-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'selfie-prompt-center-fallback-i18n-key': string | null;
      'selfie-prompt-center-translations'?: Translation[];
      'selfie-prompt-default-value': string | null;
      'selfie-prompt-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'selfie-prompt-fallback-i18n-key': string | null;
      'selfie-prompt-left-default-value': string | null;
      'selfie-prompt-left-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'selfie-prompt-left-fallback-i18n-key': string | null;
      'selfie-prompt-left-translations'?: Translation[];
      'selfie-prompt-right-default-value': string | null;
      'selfie-prompt-right-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'selfie-prompt-right-fallback-i18n-key': string | null;
      'selfie-prompt-right-translations'?: Translation[];
      'selfie-prompt-translations'?: Translation[];
      'selfie-title-default-value': string | null;
      'selfie-title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'selfie-title-fallback-i18n-key': string | null;
      'selfie-title-translations'?: Translation[];
      'stillfie-hint-left-default-value': string | null;
      'stillfie-hint-left-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'stillfie-hint-left-fallback-i18n-key': string | null;
      'stillfie-hint-left-translations'?: Translation[];
      'stillfie-hint-right-default-value': string | null;
      'stillfie-hint-right-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'stillfie-hint-right-fallback-i18n-key': string | null;
      'stillfie-hint-right-translations'?: Translation[];
      'stillfie-hint-wrong-default-value': string | null;
      'stillfie-hint-wrong-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'stillfie-hint-wrong-fallback-i18n-key': string | null;
      'stillfie-hint-wrong-translations'?: Translation[];
      'stillfie-title-default-value': string | null;
      'stillfie-title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'stillfie-title-fallback-i18n-key': string | null;
      'stillfie-title-translations'?: Translation[];
      'camera-permissions-btn-cancel-translations'?: Translation[];
      'camera-permissions-btn-cancel-default-value': string | null;
      'camera-permissions-btn-cancel-fallback-i18n-key': string | null;
      'camera-permissions-btn-cancel-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'camera-permissions-btn-continue-mobile-translations'?: Translation[];
      'camera-permissions-btn-continue-mobile-default-value': string | null;
      'camera-permissions-btn-continue-mobile-fallback-i18n-key': string | null;
      'camera-permissions-btn-continue-mobile-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'camera-permissions-btn-continue-web-translations'?: Translation[];
      'camera-permissions-btn-continue-web-default-value': string | null;
      'camera-permissions-btn-continue-web-fallback-i18n-key': string | null;
      'camera-permissions-btn-continue-web-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'camera-permissions-prompt-translations'?: Translation[];
      'camera-permissions-prompt-default-value': string | null;
      'camera-permissions-prompt-fallback-i18n-key': string | null;
      'camera-permissions-prompt-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'camera-permissions-title-translations'?: Translation[];
      'camera-permissions-title-default-value': string | null;
      'camera-permissions-title-fallback-i18n-key': string | null;
      'camera-permissions-title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'microphone-permissions-btn-cancel-translations'?: Translation[];
      'microphone-permissions-btn-cancel-default-value': string | null;
      'microphone-permissions-btn-cancel-fallback-i18n-key': string | null;
      'microphone-permissions-btn-cancel-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'microphone-permissions-btn-continue-mobile-translations'?: Translation[];
      'microphone-permissions-btn-continue-mobile-default-value': string | null;
      'microphone-permissions-btn-continue-mobile-fallback-i18n-key': string | null;
      'microphone-permissions-btn-continue-mobile-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'microphone-permissions-btn-continue-web-translations'?: Translation[];
      'microphone-permissions-btn-continue-web-default-value': string | null;
      'microphone-permissions-btn-continue-web-fallback-i18n-key': string | null;
      'microphone-permissions-btn-continue-web-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'microphone-permissions-prompt-translations'?: Translation[];
      'microphone-permissions-prompt-default-value': string | null;
      'microphone-permissions-prompt-fallback-i18n-key': string | null;
      'microphone-permissions-prompt-fallback-i18n-dynamic-variables': I18nDynamicVariables;
      'microphone-permissions-title-translations'?: Translation[];
      'microphone-permissions-title-default-value': string | null;
      'microphone-permissions-title-fallback-i18n-key': string | null;
      'microphone-permissions-title-fallback-i18n-dynamic-variables': I18nDynamicVariables;
    };
  };
}

export interface InquiryTemplateVersionStepSelfieReflectionConfig extends CombinedStepBaseConfig {
  'field-key-selfie': string;
  'web-camera-allow-stillfie-fallback': boolean;
  'enabled-capture-options-desktop': SelfieCaptureOptionDesktop[];
  'enabled-capture-options-mobile': SelfieCaptureOptionMobile[];
  localizations: DeviceHandoffConfigLocalizations;
}

export interface InquiryTemplateVersionStepUiConfig {
  'back-step'?: string;
  'hide-cancel-button'?: boolean;
  components: UiComponentConfig[];
  statuses: InquiryTemplateVersionStepStatus[];
}

export type InquiryTemplateVersionStepIntegrationConfig = TransitionConfigAttributes & {
  'back-step'?: string;
  statuses: InquiryTemplateVersionStepStatus[];
  localizations: Record<string, LocalizationWithI18nFallbackMap>;
};

export type InquiryTemplateVersionStepVerifyWithPersonaConfig = TransitionConfigAttributes & {
  'back-step'?: string;
  statuses: InquiryTemplateVersionStepStatus[];
  localizations: Record<string, LocalizationWithI18nFallbackMap>;
};

export type InquiryTemplateVersionStepLoopConfig = TransitionConfigAttributes & {
  statuses: InquiryTemplateVersionStepStatus[];
  steps: InquiryTemplateVersionStep[];
};

export interface InquiryTemplateVersionStepComplete extends InquiryTemplateVersionStepBase {
  type: UiStepType.Complete;
  config: InquiryTemplateVersionStepCompleteConfig;
}

export interface InquiryTemplateVersionStepDocument extends InquiryTemplateVersionStepBase {
  type: UiStepType.Document;
  config: InquiryTemplateVersionStepDocumentConfig;
}

export interface InquiryTemplateVersionStepGovernmentId extends InquiryTemplateVersionStepBase {
  type: UiStepType.GovernmentId;
  config: InquiryTemplateVersionStepGovernmentIdConfig;
}

export interface InquiryTemplateVersionStepSelfie extends InquiryTemplateVersionStepBase {
  type: UiStepType.Selfie;
  config: InquiryTemplateVersionStepSelfieConfig;
}

export interface InquiryTemplateVersionStepSelfieReflection extends InquiryTemplateVersionStepBase {
  type: UiStepType.SelfieReflection;
  config: InquiryTemplateVersionStepSelfieConfig;
}

export interface InquiryTemplateVersionStepUi extends InquiryTemplateVersionStepBase {
  type: UiStepType.Ui;
  config: InquiryTemplateVersionStepUiConfig;
}

export interface InquiryTemplateVersionStepIntegration extends InquiryTemplateVersionStepBase {
  type: UiStepType.Integration;
  config: InquiryTemplateVersionStepIntegrationConfig;
}

export interface InquiryTemplateVersionStepVerifyWithPersona
  extends InquiryTemplateVersionStepBase {
  type: UiStepType.VerifyWithPersona;
  config: InquiryTemplateVersionStepVerifyWithPersonaConfig;
}

export interface InquiryTemplateVersionStepLoop extends InquiryTemplateVersionStepBase {
  type: UiStepType.Loop;
  config: InquiryTemplateVersionStepLoopConfig;
}

export type InquiryTemplateVersionStep =
  | InquiryTemplateVersionStepComplete
  | InquiryTemplateVersionStepDocument
  | InquiryTemplateVersionStepIntegration
  | InquiryTemplateVersionStepVerifyWithPersona
  | InquiryTemplateVersionStepGovernmentId
  | InquiryTemplateVersionStepSelfie
  | InquiryTemplateVersionStepSelfieReflection
  | InquiryTemplateVersionStepUi
  | InquiryTemplateVersionStepLoop;

export type InquiryTemplateVersionCombinedStep =
  | InquiryTemplateVersionStepDocument
  | InquiryTemplateVersionStepGovernmentId
  | InquiryTemplateVersionStepSelfie
  | InquiryTemplateVersionStepSelfieReflection;

export function isCombinedStepConfig(
  step: InquiryTemplateVersionStep | null | undefined,
): step is InquiryTemplateVersionCombinedStep {
  switch (step?.type) {
    case UiStepType.Document:
    case UiStepType.GovernmentId:
    case UiStepType.Selfie:
    case UiStepType.SelfieReflection:
      return true;
    default:
      return false;
  }
}

export function isGovernmentIdStepConfig(
  step: InquiryTemplateVersionStep | undefined,
): step is InquiryTemplateVersionStepGovernmentId {
  switch (step?.type) {
    case UiStepType.GovernmentId:
      return true;
    default:
      return false;
  }
}

export function isUiStepConfig(
  step: InquiryTemplateVersionStep | null | undefined,
): step is InquiryTemplateVersionStepUi {
  switch (step?.type) {
    case UiStepType.Ui:
      return true;
    default:
      return false;
  }
}

export function isIntegrationStepConfig(
  step: InquiryTemplateVersionStep | null | undefined,
): step is InquiryTemplateVersionStepIntegration {
  switch (step?.type) {
    case UiStepType.Integration:
      return true;
    default:
      return false;
  }
}

export function isVerifyWithPersonaStepConfig(
  step: InquiryTemplateVersionStep | undefined,
): step is InquiryTemplateVersionStepVerifyWithPersona {
  switch (step?.type) {
    case UiStepType.VerifyWithPersona:
      return true;
    default:
      return false;
  }
}

export function isLoopStepConfig(
  step: InquiryTemplateVersionStep | undefined,
): step is InquiryTemplateVersionStepLoop {
  switch (step?.type) {
    case UiStepType.Loop:
      return true;
    default:
      return false;
  }
}

export enum BiometricConsentComplianceFailureReasons {
  ConsentTextNotPrecedingBiometricVerifications = 'consent_text_not_preceding_biometric_verifications',
  ConsentTextNotPrecedingSubmitButton = 'consent_text_not_preceding_submit_button',
}

export interface InvalidStep {
  name: string;
  reason: BiometricConsentComplianceFailureReasons;
}

type BiometricAutoMigrationStatus = 'migrated' | 'migratable' | 'not_migratable' | 'not_applicable';
export interface BiometricConsentCompliance {
  'ignore-validation': boolean;
  validation: {
    'biometric-consent-text-required': boolean;
    'invalid-steps': InvalidStep[];
  };
}

export enum EnhancedUsageType {
  QuotaExceeded = 'quota_exceeded_usage',
}

export enum Feature {
  Step = 'step',
  Asset = 'asset',
  Action = 'action',
  EnhancedComponent = 'enhanced_component',
  Conditional = 'conditional',
  ConditionalBranch = 'conditional_branch',
  Module = 'module',
  HeaderLogo = 'header_logo',
}

export interface QuotaExceededUsage {
  feature: Feature;
  type: EnhancedUsageType.QuotaExceeded;
  'usage-count': number;
  'usage-count-quota': number;
}

export type EnhancedUsage = QuotaExceededUsage;

export enum InquiryTemplateVersionTier {
  Basic = 'basic',
  Enhanced = 'enhanced',
}

export interface InquiryTemplateVersionAttributes {
  'allowed-hosts': string[];
  'biometric-auto-migration-status': BiometricAutoMigrationStatus;
  'biometric-consent-compliance': BiometricConsentCompliance;
  'created-at': string;
  'disable-client-create': boolean;
  'disable-device-handoff-inputs-if-prefilled': boolean;
  'enable-device-handback': boolean;
  'enabled-locales': InquiryIso6391LanguageCode[];
  'enhanced-usages': EnhancedUsage[];
  'expiration-intervals': ExpirationIntervals;
  'max-session-limit': number | null;
  'field-key-device-handoff-email': string | null;
  'field-key-device-handoff-phone-number': string | null;
  'field-schema': FieldSchema[];
  'govid-design-version'?: number;
  'last-updater': string;
  'lock-version': number;
  'name-display'?: string;
  'publish-approved': boolean;
  'redirect-query-string-config': RedirectQueryStringConfig;
  'redirect-uri': string | null;
  'updated-at': string;
  description?: string;
  name: string;
  status: InquiryTemplateVersionStatus;
  steps: InquiryTemplateVersionStep[];
  theme: ThemeAttributes;
  tier: InquiryTemplateVersionTier;
  'has-reusable-persona-config': boolean;
  'has-custom-reusable-persona-config': boolean;
}

export interface PartialInquiryTemplateVersionAttributes {
  name: string;
  'name-display'?: string;
  description?: string;
  tier: InquiryTemplateVersionTier;
  status: InquiryTemplateVersionStatus;
  'updated-at'?: string;
  'published-at'?: string;
  'edited-at'?: string;
  live: boolean;
  'last-updater'?: string;
}

export enum InquiryTemplateVersionStatus {
  Draft = 'draft',
  Published = 'published',
}

export interface ExpirationIntervals {
  'expiration-after-create-interval-seconds'?: number;
  'expiration-after-start-interval-seconds'?: number;
  'expiration-after-resume-interval-seconds'?: number;
  'one-time-link-expiration-seconds'?: number;
}

export interface InquiryTemplateVersionRelationships {
  'inquiry-template': ApiRelationship<ApiModel.InquiryTemplate>;
  'theme-set'?: ApiRelationship<ApiModel.ThemeSet>;
}

export type InquiryTemplateVersion = ApiResource<
  InquiryTemplateVersionAttributes,
  InquiryTemplateVersionRelationships,
  ApiModel.InquiryTemplateVersion
>;

/**
 * Partial of InquiryTemplateVersion returned by InquiriesController#show
 */
export type InquiryShowViewInquiryTemplateVersion = ApiResource<
  Pick<InquiryTemplateVersionAttributes, 'field-schema'>,
  InquiryTemplateVersionRelationships,
  ApiModel.InquiryTemplateVersion
>;

export type PartialInquiryTemplateVersion = ApiResource<
  PartialInquiryTemplateVersionAttributes,
  {},
  ApiModel.PartialInquiryTemplateVersion
>;

/**
 * Map of related entities for the inquiry template version.
 */
export interface InquiryTemplateVersionSchema {
  'workflow-versions': { data: WorkflowVersion[] };
  'verification-template-versions': { data: VerificationTemplateVersion[] };
  'match-requirement-profiles': { data: MatchRequirementProfile[] };
}

export type BaseInquiryTemplateVersionData = {
  'field-schema': FieldSchema[];
  'workflow-versions': { data: WorkflowVersion[] };
  steps: InquiryTemplateVersionStep[];
  'steps-preview': StepPreview[];
};

export type InquiryTemplateVersionDiff = {
  'country-codes': CountryCodeMap;
  'disable-client-create': ValueDiff<boolean>;
  'enabled-locales': SetDiff<InquiryLanguage>;
  'expiration-intervals': HashDiff<keyof ExpirationIntervals>;
  'field-schema': ObjectArrayDiff<FieldSchema>;
  'redirect-query-string-config': HashDiff<keyof RedirectQueryStringConfig>;
  'redirect-uri': ValueDiff<string>;
  'verification-template-versions': VerificationTemplateVersionArrayDiff;
  'workflow-versions': WorkflowVersionArrayDiff;
  steps: StepsArrayDiff;
  theme: HashDiff<string>;
  connectors: ConnectorsDiff;
};
